import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Cambiar de página deja el scroll arriba.
 *
 * Va **una sola vez en el layout**, no pantalla por pantalla: con el componente montado (de
 * una nota a otra, de una sección a otra) el navegador conserva la posición y el lector
 * aterrizaba en la mitad de la nota nueva.
 *
 * Depende del **pathname solo**: el query string queda afuera porque la búsqueda reescribe
 * `?q=` mientras se tipea y saltaría la página en cada tecla. Con hash tampoco salta — el
 * ancla es justamente adónde quiere ir el lector.
 *
 * Es un efecto, nunca el camino de render: `window` en render rompe el SSR.
 */
export function useVolverAlTope(): void {
  const { pathname, hash } = useLocation();
  /**
   * La página que ya se atendió. Arranca en la actual para **no** tocar el scroll al
   * montar: ahí la posición la pone el navegador (restauración al recargar, ancla de la
   * URL) y pisarla sería sacar al lector de donde estaba.
   */
  const atendida = useRef(pathname);

  useEffect(() => {
    if (atendida.current === pathname) return;
    atendida.current = pathname;
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
}
